import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, ImagePlus, Plus, Trash2 } from "lucide-react";
import { API, resolveImg } from "@/lib/site";
import { ICON_NAMES } from "@/components/Icon";

const LABELS = {
  hero: "Главный экран", marquee: "Бегущая строка", about: "О компании", founder: "Основатель", stats: "Цифры",
  directions: "Направления", why: "Почему мы", whyImage: "Фото команды", stages: "Этапы работы", cases: "Кейсы", contacts: "Контакты",
  eyebrow: "Надзаголовок", line1: "Заголовок — строка 1", line2: "Заголовок — строка 2 (золотая)", line3: "Заголовок — строка 3",
  subtitle: "Подзаголовок", image: "Фото", image2: "Второе фото", title: "Заголовок", lead: "Лид-абзац", text: "Текст",
  name: "Имя", role: "Должность", quote: "Цитата", regalia: "Регалии", value: "Число", suffix: "Суффикс (+, %)", label: "Подпись",
  slug: "Адрес страницы (латиницей)", short: "Краткое описание (карточка)", pageTitle: "Заголовок страницы", intro: "Описание услуги",
  categories: "Подкатегории", included: "Что входит в услугу", icon: "Иконка", caseIds: "Кейсы этого направления (ID кейсов)",
  items: "Пункты", id: "ID кейса (латиницей, без пробелов)", featured: "Показывать крупно на главной", year: "Год", images: "Фотографии (первая — обложка)",
  youtube: "Ссылка на видео YouTube (откроется прямо на сайте)",
  steps: "Этапы работы (свои для этого направления)", stars: "Звёзды (фото с артистами)", note: "Подпись под фото",
  outroTitle: "Финальная карточка — заголовок", outroText: "Финальная карточка — текст", phone: "Телефон", whatsapp: "Номер WhatsApp (только цифры)", email: "E-mail", instagram: "Instagram (без @)", address: "Адрес",
};
const IMAGE_KEYS = ["image", "image2", "whyImage"];
const LANG_NAMES = { ru: "RU", en: "EN", ky: "KG" };

export const isI18n = (v) => v && typeof v === "object" && !Array.isArray(v) && "ru" in v && "en" in v;
const blank = (v) => {
  if (isI18n(v)) return { ru: "", en: "", ky: "" };
  if (Array.isArray(v)) return [];
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, blank(x)]));
  if (typeof v === "boolean") return false;
  return "";
};
const inputCls = "w-full rounded-lg border border-[#d9c8a8] bg-white px-3 py-2 text-sm text-[#1d1712] focus:border-[#b8893b] focus:outline-none focus:ring-2 focus:ring-[#b8893b]/20";

const ImageField = ({ value, onChange, path, authHeaders }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, { headers: authHeaders, withCredentials: true });
      onChange(res.data?.url || value);
      toast.success("Фото загружено — не забудьте сохранить");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка загрузки");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-start gap-3">
      <div className="h-20 w-28 shrink-0 overflow-hidden rounded-lg border border-[#d9c8a8] bg-[#f3eadb]">
        {value ? <img src={resolveImg(value)} alt="" className="h-full w-full object-cover" /> : null}
      </div>
      <div className="flex-1 space-y-2">
        <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="/media/... или ссылка" data-testid={`admin-img-url-${path}`} />
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#1d1712] px-3 py-1.5 text-xs font-semibold text-[#f6ecd9] disabled:opacity-50" data-testid={`admin-img-upload-${path}`}>
          <ImagePlus className="h-3.5 w-3.5" /> {busy ? "Загрузка…" : "Загрузить фото"}
        </button>
      </div>
    </div>
  );
};

const I18nField = ({ value, onChange, path }) => {
  const long = Object.values(value).some((s) => (s || "").length > 70);
  return (
    <div className="grid gap-2 md:grid-cols-3">
      {["ru", "en", "ky"].map((l) => (
        <label key={l} className="block">
          <span className="mb-1 block text-[0.65rem] font-bold tracking-widest text-[#8a6424]">{LANG_NAMES[l]}</span>
          {long ? (
            <textarea rows={4} className={inputCls} value={value[l] || ""} onChange={(e) => onChange({ ...value, [l]: e.target.value })} data-testid={`admin-field-${path}-${l}`} />
          ) : (
            <input className={inputCls} value={value[l] || ""} onChange={(e) => onChange({ ...value, [l]: e.target.value })} data-testid={`admin-field-${path}-${l}`} />
          )}
        </label>
      ))}
    </div>
  );
};

const ArrayField = ({ value, onChange, path, fieldKey, authHeaders }) => {
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => onChange([...value, value.length ? blank(value[value.length - 1]) : ""]);
  return (
    <div className="space-y-3">
      {value.map((item, i) => (
        <div key={i} className="rounded-xl border border-[#e4d6bb] bg-[#fdfaf4] p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-[#8a6424]">#{i + 1}{item && typeof item === "object" && !isI18n(item) && (item.title?.ru || item.id) ? ` · ${item.title?.ru || item.id}` : ""}</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} className="rounded p-1 hover:bg-[#f3eadb]" aria-label="up"><ChevronUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(i, 1)} className="rounded p-1 hover:bg-[#f3eadb]" aria-label="down"><ChevronDown className="h-4 w-4" /></button>
              <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} className="rounded p-1 text-red-700 hover:bg-red-50" data-testid={`admin-remove-${path}-${i}`} aria-label="remove"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          <Field value={item} onChange={(v) => onChange(value.map((x, k) => (k === i ? v : x)))} path={`${path}-${i}`} fieldKey={fieldKey === "images" ? "image" : ""} authHeaders={authHeaders} />
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-2 rounded-lg border border-dashed border-[#b8893b] px-3 py-1.5 text-xs font-semibold text-[#8a6424] hover:bg-[#f3eadb]" data-testid={`admin-add-${path}`}>
        <Plus className="h-3.5 w-3.5" /> Добавить
      </button>
    </div>
  );
};

export const Field = ({ value, onChange, path, fieldKey = "", authHeaders }) => {
  if (isI18n(value)) return <I18nField value={value} onChange={onChange} path={path} />;
  if (Array.isArray(value)) return <ArrayField value={value} onChange={onChange} path={path} fieldKey={fieldKey} authHeaders={authHeaders} />;
  if (typeof value === "boolean") {
    return <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 accent-[#b8893b]" data-testid={`admin-check-${path}`} />;
  }
  if (value && typeof value === "object") {
    return (
      <div className="space-y-5">
        {Object.entries(value).map(([k, v]) => (
          <div key={k}>
            <p className="mb-2 text-sm font-semibold text-[#1d1712]">{LABELS[k] || k}</p>
            <Field value={v} onChange={(nv) => onChange({ ...value, [k]: nv })} path={`${path}-${k}`} fieldKey={k} authHeaders={authHeaders} />
          </div>
        ))}
      </div>
    );
  }
  if (IMAGE_KEYS.includes(fieldKey)) return <ImageField value={value} onChange={onChange} path={path} authHeaders={authHeaders} />;
  if (fieldKey === "icon") {
    return (
      <select className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`admin-icon-${path}`}>
        {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    );
  }
  return <input className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`admin-input-${path}`} />;
};

export const SECTION_LABELS = LABELS;
